import type { StructureDetailDto } from '@/endpoints/models'
import type { StructureMetadataDtoRead } from '@/rtkApi/mddApiSliceGeneratedV2'
import { isPrivilegedUser } from '@/utils/locking'

const ALL_STRUCTURE_PERMISSIONS = [
	'VIEW',
	'EDIT',
	'MANAGE_ACCESS',
	'FORCE_UNLOCK',
] as const

/**
 * Adapts GET /v2/structure/{id} metadata into the legacy StructureDetailDto shape
 * used by Redux NODE_LOAD. Large/sensitive fields (data, workingData) stay unset until
 * feature-specific loaders fetch them.
 */
export function mapStructureMetadataToStructureDetailDto(
	meta: StructureMetadataDtoRead,
): StructureDetailDto {
	const privileged = isPrivilegedUser()
	const permissions =
		privileged &&
		(!Array.isArray(meta.permissions) || meta.permissions.length === 0)
			? [...ALL_STRUCTURE_PERMISSIONS]
			: meta.permissions
	// If the API omits `permissions`, do not assume edit is allowed (VIEW-only was incorrectly treated as editable).
	const canEdit =
		privileged ||
		(Array.isArray(permissions) && permissions.includes('EDIT'))

	return {
		id: meta.id ?? 0,
		type: (meta.type ?? 'FOLDER') as StructureDetailDto.TypeEnum,
		name: meta.name ?? '',
		code: meta.code ?? '',
		uuid: meta.uuid,
		parentStructureId: meta.parent?.id,
		hasChildren: false,
		companyId: 0,
		canEdit,
		structurePermissions: permissions,
		lockUserId: meta.lockedFor?.id,
		lockUserName: meta.lockedFor?.fullName,
		lockTime: meta.lockedTime,
		orderPriority: meta.orderPriority,
		status: meta.status,
	}
}
